import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from "@material-ui/core";
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Settings as TestIcon,
  Info as InfoIcon,
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import ToggleSwitch from "../ToggleSwitch";

const t = (key) => i18n.t(`birthday.${key}`);

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 760,
    margin: "0 auto",
  },
  /* Section card */
  card: {
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.09)",
    borderRadius: 14,
    marginBottom: theme.spacing(2),
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { background: "rgba(0,0,0,0.02)" },
  },
  cardHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: 15,
  },
  cardIcon: {
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  expandIcon: {
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandIconOpen: { transform: "rotate(180deg)" },
  cardBody: {
    padding: "4px 18px 18px",
    borderTop: "1px solid rgba(0,0,0,0.07)",
  },
  settingBlock: {
    paddingTop: theme.spacing(2),
  },
  helpText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 4,
    lineHeight: 1.5,
  },
  messageField: {
    marginTop: theme.spacing(1),
  },
  preview: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 14px",
    marginTop: 10,
  },
  previewLabel: {
    fontSize: 11,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontWeight: 600,
    marginBottom: 6,
  },
  variableChip: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: "2px 7px",
    borderRadius: 4,
    fontSize: "0.72rem",
    fontFamily: "monospace",
    margin: "0 2px",
  },
  infoBox: {
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    border: "1px solid #bfdbfe",
    borderRadius: 10,
    padding: "12px 16px",
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },
  infoIcon: { color: "#1976d2", fontSize: 20, marginTop: 2 },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 8,
  },
  saveBtn: {
    background: "linear-gradient(135deg, #1976d2, #1565c0)",
    color: "#fff",
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 10,
    padding: "10px 28px",
    fontSize: 14,
    boxShadow: "0 2px 10px rgba(25,118,210,0.25)",
    "&:hover": { boxShadow: "0 4px 16px rgba(25,118,210,0.35)", transform: "translateY(-1px)" },
  },
  testBtn: {
    background: "linear-gradient(135deg, #ff9800, #f57c00)",
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    fontSize: 13,
    "&:hover": { boxShadow: "0 4px 12px rgba(255,152,0,0.35)" },
  },
}));

const BirthdaySettings = () => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ user: true, contact: true, general: true });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get("/birthdays/settings");
      const s = data.data || {};
      const isPortuguese = (msg) => msg && (msg.includes("Parabéns") || msg.includes("Hoje") || msg.includes("felicidade"));
      if (isPortuguese(s.userBirthdayMessage)) {
        s.userBirthdayMessage = "🎉 ¡Feliz cumpleaños, {nombre}! Que tengas un día increíble y lleno de alegría.";
      }
      if (isPortuguese(s.contactBirthdayMessage)) {
        s.contactBirthdayMessage = "🎂 ¡Hola {nombre}! Hoy cumples {edad} años. ¡Feliz cumpleaños! Que sea un día muy especial.";
      }
      setSettings(s);
    } catch {
      toast.error(t("toasts.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) =>
    setSettings((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/birthdays/settings", settings);
      toast.success(t("toasts.saveSuccess"));
    } catch {
      toast.error(t("toasts.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (messageType) => {
    const contactId = prompt(t("toasts.testPrompt"));
    if (!contactId) return;
    try {
      await api.post("/birthdays/test-message", { contactId: parseInt(contactId), messageType });
      toast.success(t("toasts.testSuccess"));
    } catch {
      toast.error(t("toasts.testError"));
    }
  };

  const toggle = (section) =>
    setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  const renderPreview = (message) => {
    if (!message) return null;
    const html = message
      .replace(/{nome}/g, `<span class="${classes.variableChip}">Juan García</span>`)
      .replace(/{nombre}/g, `<span class="${classes.variableChip}">Juan García</span>`)
      .replace(/{idade}/g, `<span class="${classes.variableChip}">30</span>`)
      .replace(/{edad}/g, `<span class="${classes.variableChip}">30</span>`);
    return (
      <div className={classes.preview}>
        <div className={classes.previewLabel}>{t("preview")}</div>
        <Typography variant="body2" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    );
  };

  const SectionCard = ({ sectionKey, icon: Icon, titleKey, children }) => (
    <div className={classes.card}>
      <div className={classes.cardHeader} onClick={() => toggle(sectionKey)}>
        <div className={classes.cardHeaderLeft}>
          <Icon className={classes.cardIcon} />
          {t(`sections.${titleKey}`)}
        </div>
        <IconButton size="small">
          <ExpandMoreIcon
            className={`${classes.expandIcon} ${expanded[sectionKey] ? classes.expandIconOpen : ""}`}
          />
        </IconButton>
      </div>
      <Collapse in={expanded[sectionKey]}>
        <div className={classes.cardBody}>{children}</div>
      </Collapse>
    </div>
  );

  if (loading) {
    return (
      <Box className={classes.root}>
        <Typography color="textSecondary">Cargando...</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Usuarios */}
      <SectionCard sectionKey="user" icon={PersonIcon} titleKey="users">
        <div className={classes.settingBlock}>
          <ToggleSwitch
            checked={!!settings?.userBirthdayEnabled}
            onChange={(val) => handleChange("userBirthdayEnabled", val)}
            label={t("users.enable")}
          />
          <div className={classes.helpText}>{t("users.enableHelper")}</div>
        </div>

        <div className={classes.settingBlock}>
          <ToggleSwitch
            checked={!!settings?.createAnnouncementForUsers}
            onChange={(val) => handleChange("createAnnouncementForUsers", val)}
            disabled={!settings?.userBirthdayEnabled}
            label={t("users.announcement")}
          />
          <div className={classes.helpText}>{t("users.announcementHelper")}</div>
        </div>

        <div className={classes.settingBlock}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t("users.messageLabel")}
            value={settings?.userBirthdayMessage || ""}
            onChange={(e) => handleChange("userBirthdayMessage", e.target.value)}
            disabled={!settings?.userBirthdayEnabled}
            className={classes.messageField}
            helperText={t("users.messageHelper")}
            variant="outlined"
            size="small"
          />
          {renderPreview(settings?.userBirthdayMessage)}
        </div>
      </SectionCard>

      {/* Contactos */}
      <SectionCard sectionKey="contact" icon={PhoneIcon} titleKey="contacts">
        <div className={classes.settingBlock}>
          <ToggleSwitch
            checked={!!settings?.contactBirthdayEnabled}
            onChange={(val) => handleChange("contactBirthdayEnabled", val)}
            label={t("contacts.enable")}
          />
          <div className={classes.helpText}>{t("contacts.enableHelper")}</div>
        </div>

        <div className={classes.settingBlock}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t("contacts.messageLabel")}
            value={settings?.contactBirthdayMessage || ""}
            onChange={(e) => handleChange("contactBirthdayMessage", e.target.value)}
            disabled={!settings?.contactBirthdayEnabled}
            className={classes.messageField}
            helperText={t("contacts.messageHelper")}
            variant="outlined"
            size="small"
          />
          {renderPreview(settings?.contactBirthdayMessage)}

          {settings?.contactBirthdayEnabled && (
            <Button
              variant="contained"
              className={classes.testBtn}
              startIcon={<TestIcon />}
              onClick={() => handleTest("contact")}
              size="small"
              style={{ marginTop: 12 }}
            >
              {t("contacts.testButton")}
            </Button>
          )}
        </div>
      </SectionCard>

      {/* General */}
      <SectionCard sectionKey="general" icon={ScheduleIcon} titleKey="general">
        <Grid container spacing={2} style={{ paddingTop: 8 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              type="time"
              label={t("general.sendTime")}
              value={(settings?.sendBirthdayTime || "09:00:00").slice(0, 5)}
              onChange={(e) => {
                const v = e.target.value;
                handleChange("sendBirthdayTime", v ? `${v}:00` : "09:00:00");
              }}
              InputLabelProps={{ shrink: true }}
              helperText={t("general.sendTimeHelper")}
              variant="outlined"
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>{t("general.whatsappConnection")}</InputLabel>
              <Select
                value={settings?.whatsappId || ""}
                onChange={(e) => handleChange("whatsappId", e.target.value || null)}
                label={t("general.whatsappConnection")}
                disabled={!settings?.contactBirthdayEnabled}
              >
                <MenuItem value="">
                  <em>{t("general.selectConnection")}</em>
                </MenuItem>
                {whatsApps?.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    <span
                      className={classes.statusDot}
                      style={{ background: w.status === "CONNECTED" ? "#16a34a" : "#dc2626" }}
                    />
                    {w.name} ({w.status})
                  </MenuItem>
                ))}
              </Select>
              <div className={classes.helpText} style={{ marginTop: 6 }}>
                {t("general.connectionHelper")}
              </div>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <ToggleSwitch
              checked={!!settings?.createAnnouncementForUsers}
              onChange={(val) => handleChange("createAnnouncementForUsers", val)}
              label={t("general.internalAnnouncement")}
            />
            <div className={classes.helpText}>{t("general.internalAnnouncementHelper")}</div>
          </Grid>
        </Grid>

        <div className={classes.infoBox}>
          <InfoIcon className={classes.infoIcon} />
          <div>
            <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 6 }}>
              {t("general.variables")}
            </Typography>
            <Typography variant="body2" component="div" style={{ lineHeight: 1.8 }}>
              • <code>{"{nombre}"}</code> {t("general.variableName")}<br />
              • <code>{"{edad}"}</code> {t("general.variableAge")}
            </Typography>
          </div>
        </div>
      </SectionCard>

      {/* Guardar */}
      <Box textAlign="center" mt={3} mb={2}>
        <Button
          variant="contained"
          className={classes.saveBtn}
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
        >
          {saving ? t("saving") : t("save")}
        </Button>
      </Box>
    </Box>
  );
};

export default BirthdaySettings;
